import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import DashboardSettings from './components/DashboardSettings';
import UserManagement from './components/UserManagement';
import ApiKeyManagement from './components/ApiKeyManagement';
import CompanyManagement from './components/CompanyManagement';
import UserProfile from './components/UserProfile';
import Navbar from './components/Navbar';
import useStore from './store/useStore';
import { metricsAPI, dashboardsAPI } from './services/api';
import websocketService from './services/websocket';
import { useDashboardStale } from './hooks/useDashboardStale';

// CRITICAL: Define route protection components OUTSIDE App to prevent recreation on every render
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const ProtectedAdminRoute = ({ children }) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const user = useStore((state) => state.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'admin' && user?.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
};

const ProtectedSuperAdminRoute = ({ children }) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const user = useStore((state) => state.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
};

function App() {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const token = useStore((state) => state.token);
  const currentDashboardId = useStore((state) => state.currentDashboardId);
  const currentCompany = useStore((state) => state.currentCompany);

  const setMetrics = useStore((state) => state.setMetrics);
  const updateMetric = useStore((state) => state.updateMetric);
  const setDashboards = useStore((state) => state.setDashboards);
  const setCurrentDashboard = useStore((state) => state.setCurrentDashboard);
  const setWsConnected = useStore((state) => state.setWsConnected);
  const setLastUpdate = useStore((state) => state.setLastUpdate);
  const setDashboardLastUpdate = useStore((state) => state.setDashboardLastUpdate);

  const isStale = useDashboardStale();

  // Initialize data and WebSocket connection
  useEffect(() => {
    if (!isAuthenticated) return;

    // Load initial data
    const loadData = async () => {
      try {
        // Load all dashboards
        const dashboardsResponse = await dashboardsAPI.getAll();
        let loadedDashboardId = null;

        if (dashboardsResponse.success) {
          // Filter dashboards for current company
          if (currentCompany && currentCompany.id) {
            const companyDashboards = dashboardsResponse.data.filter(d => d.company_id === currentCompany.id);
            setDashboards(companyDashboards);

            // Find which dashboard to load
            let dashboardToLoad = null;
            if (currentDashboardId) {
              // Try to find the saved dashboard
              dashboardToLoad = companyDashboards.find(d => d.id === parseInt(currentDashboardId));
            }

            // If no saved dashboard or it doesn't belong to current company, use default or first
            if (!dashboardToLoad && companyDashboards.length > 0) {
              dashboardToLoad = companyDashboards.find(d => d.is_default) || companyDashboards[0];
            }

            // Load the full dashboard data (including widgets) by ID
            if (dashboardToLoad) {
              const fullDashboardResponse = await dashboardsAPI.getById(dashboardToLoad.id);
              if (fullDashboardResponse.success) {
                setCurrentDashboard(fullDashboardResponse.data);
                loadedDashboardId = dashboardToLoad.id;
              }
            } else if (companyDashboards.length === 0) {
              // No dashboards available for this company - clear current dashboard
              setCurrentDashboard(null);
            }
          } else {
            // No company selected, load all dashboards (shouldn't happen normally)
            setDashboards(dashboardsResponse.data);
          }
        }

        // Load latest metrics for current company
        if (currentCompany && currentCompany.id) {
          const metricsResponse = await metricsAPI.getLatestAll(currentCompany.id);
          if (metricsResponse.success) {
            const metricsMap = {};
            let mostRecentTimestamp = null;

            metricsResponse.data.forEach((metric) => {
              metricsMap[metric.metric_key] = metric;

              // Track the most recent timestamp from all metrics
              if (metric.timestamp) {
                const metricTime = new Date(metric.timestamp);
                if (!mostRecentTimestamp || metricTime > mostRecentTimestamp) {
                  mostRecentTimestamp = metricTime;
                }
              }
            });

            setMetrics(metricsMap);

            // Set initial timestamp for the dashboard
            // Real-time updates will come from WebSocket
            if (mostRecentTimestamp && loadedDashboardId) {
              setDashboardLastUpdate(loadedDashboardId, mostRecentTimestamp);
            }
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadData();

    // Connect WebSocket
    websocketService.connect(token);

    // Handle incoming metric updates
    const handleMetricUpdate = (message) => {
      if (message.type === 'metric_update') {
        const { data } = message;

        // Get current company from store (not closure) to handle company switches
        const currentStoreCompany = useStore.getState().currentCompany;

        // Only update if this metric belongs to the current company
        if (currentStoreCompany && data.companyId === currentStoreCompany.id) {
          // Update metric (including service status which is now just a regular metric)
          updateMetric(data.metricKey, data);

          // Use the actual metric timestamp, not the current time
          if (data.timestamp) {
            setLastUpdate(new Date(data.timestamp));
          }
        }
      }
    };

    // Handle WebSocket connection status changes
    const handleConnectionStatusChange = (isConnected) => {
      setWsConnected(isConnected);
    };

    // Subscribe to WebSocket events
    const unsubscribeFromMessages = websocketService.onMessage(handleMetricUpdate);
    const unsubscribeFromConnectionChanges = websocketService.onConnectionChange(handleConnectionStatusChange);

    // Cleanup on unmount
    return () => {
      unsubscribeFromMessages();
      unsubscribeFromConnectionChanges();
      websocketService.disconnect();
    };
  }, [isAuthenticated, token]);

  return (
    <BrowserRouter>
      <div
        className="app"
        style={{
          backgroundColor: isStale ? 'rgba(255, 152, 0, 0.08)' : 'var(--bg-primary)',
          transition: 'background-color 0.5s ease'
        }}
      >
        {isAuthenticated && <Navbar />}
        <div className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <DashboardSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedAdminRoute>
                  <UserManagement />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/api-keys"
              element={
                <ProtectedAdminRoute>
                  <ApiKeyManagement />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/companies"
              element={
                <ProtectedSuperAdminRoute>
                  <CompanyManagement />
                </ProtectedSuperAdminRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
